/**
 * Formats an ISO 8601 timestamp into a human-readable time string.
 * e.g. "14:32" or "2:32 PM" depending on locale.
 */
export function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Returns initials from a display name (up to 2 characters).
 * e.g. "Alice Bob" → "AB", "Alice" → "AL"
 */
export function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

/**
 * Builds the WebSocket URL for a room join request.
 * Converts http(s) to ws(s) and appends the correct path + query params.
 */
export function buildWsUrl(
  slug: string,
  params: { userId: string; displayName: string; isHost: boolean },
): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;
  const wsBase = base.replace(/^http/, 'ws');
  const qs = new URLSearchParams({
    userId: params.userId,
    displayName: params.displayName,
    isAdmin: params.isHost ? '1' : '0',
  });
  return `${wsBase}/api/rooms/${slug}/join?${qs.toString()}`;
}

/**
 * Copies text to the clipboard and returns true on success.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Clamps a number between min and max (inclusive).
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
